import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { pollColorPalette } from "./utils/pollColorPalette";
import { PollResultView } from "./pollTypes";
import { ConstantSumScatterPlot, ConstantSumScatterPoint } from "./visualizations/ConstantSumScatterPlot";

interface Cluster {
  id: string;
  label: string;
  description?: string;
  image_url?: string;
  subCategories: SubCategory[];
}

interface SubCategory {
  id: string;
  label: string;
  description?: string;
}

interface PollResultsProps {
  pollId: string;
  clusters: Cluster[];
  userClusterAllocations: Record<string, number>;
  userSubAllocations: Record<string, number>;
  hasSubmitted: boolean;
  viewMode?: PollResultView;
}

interface AggregateResult {
  clusterId: string;
  subCategoryId: string | null;
  avgPoints: number;
}

type ScatterDisplayMode = "clusters_only" | "subcategories_plus_clusters";
type AllocationSummaryDisplayMode = "combined" | "clusters_only" | "most_granular";

export const PollConstantSumResults = ({
  pollId,
  clusters,
  userClusterAllocations,
  userSubAllocations,
  hasSubmitted,
  viewMode,
}: PollResultsProps) => {
  const hasAnySubcategories = clusters.some((cluster) => cluster.subCategories.length > 0);
  const [scatterDisplayMode, setScatterDisplayMode] = useState<ScatterDisplayMode>(() =>
    hasAnySubcategories ? "subcategories_plus_clusters" : "clusters_only"
  );
  const [allocationSummaryDisplayMode, setAllocationSummaryDisplayMode] = useState<AllocationSummaryDisplayMode>(() =>
    hasAnySubcategories ? "combined" : "clusters_only"
  );
  const [sortBy, setSortBy] = useState<"average" | "user">("average");
  const [results, setResults] = useState<AggregateResult[]>([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [actualResponseCount, setActualResponseCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const resolvedViewMode: PollResultView =
    viewMode === "scatter_chart" ? "scatter_chart" : "allocation_summary";

  const loadResults = useCallback(async () => {
    try {
      // First get actual response count from poll_responses table
      const { count: dbResponseCount } = await supabase
        .from("poll_responses")
        .select("*", { count: "exact", head: true })
        .eq("poll_id", pollId);
      
      setActualResponseCount(dbResponseCount || 0);

      const { data: allocations, error } = await supabase
        .from("poll_allocations")
        .select("cluster_id, sub_category_id, points, response_id, poll_responses!inner(poll_id)")
        .eq("poll_responses.poll_id", pollId);

      if (error) throw error;

      const responseIds = new Set<string>();
      allocations?.forEach((alloc) => responseIds.add(alloc.response_id));
      setTotalResponses(responseIds.size);

      if (allocations && allocations.length > 0) {
        const grouped = new Map<
          string,
          { total: number; count: number; clusterId: string; subCategoryId: string | null }
        >();

        allocations.forEach((alloc) => {
          const key = `${alloc.cluster_id}::${alloc.sub_category_id || "null"}`;
          const current = grouped.get(key) || {
            total: 0,
            count: 0,
            clusterId: alloc.cluster_id,
            subCategoryId: alloc.sub_category_id,
          };

          grouped.set(key, {
            total: current.total + alloc.points,
            count: current.count + 1,
            clusterId: current.clusterId,
            subCategoryId: current.subCategoryId,
          });
        });

        const avgResults: AggregateResult[] = [];
        grouped.forEach((value) => {
          avgResults.push({
            clusterId: value.clusterId,
            subCategoryId: value.subCategoryId,
            avgPoints: value.count > 0 ? value.total / value.count : 0,
          });
        });

        setResults(avgResults);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading results:", error);
      setLoading(false);
    }
  }, [pollId]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  useEffect(() => {
    setScatterDisplayMode((current) => {
      if (!hasAnySubcategories) return "clusters_only";
      return current;
    });
  }, [hasAnySubcategories]);

  useEffect(() => {
    setAllocationSummaryDisplayMode((current) => {
      if (!hasAnySubcategories) return "clusters_only";
      return current;
    });
  }, [hasAnySubcategories]);

  const clusterAverageMap = useMemo(() => {
    const map = new Map<string, number>();
    results.forEach((result) => {
      if (result.subCategoryId === null) {
        map.set(result.clusterId, result.avgPoints);
      }
    });
    return map;
  }, [results]);
  const subAverageMap = useMemo(() => {
    const map = new Map<string, number>();
    results.forEach((result) => {
      if (result.subCategoryId) {
        map.set(result.subCategoryId, result.avgPoints);
      }
    });
    return map;
  }, [results]);
  const getClusterAvg = (clusterId: string) =>
    clusterAverageMap.get(clusterId) ?? 0;
  const getSubAvg = (subId: string) => subAverageMap.get(subId) ?? 0;
  const sortedClusters = useMemo(() => {
    return [...clusters].sort((a, b) => {
      const aValue = sortBy === "user" ? (userClusterAllocations[a.id] || 0) : getClusterAvg(a.id);
      const bValue = sortBy === "user" ? (userClusterAllocations[b.id] || 0) : getClusterAvg(b.id);

      if (bValue !== aValue) return bValue - aValue;
      return a.label.localeCompare(b.label);
    });
  }, [clusters, getClusterAvg, sortBy, userClusterAllocations]);
  const granularItems = useMemo(() => {
    const items: Array<{
      id: string;
      type: "cluster" | "sub";
      label: string;
      parentLabel?: string;
      description?: string;
      imageUrl?: string;
      avg: number;
      user: number;
    }> = [];

    clusters.forEach((cluster) => {
      if (cluster.subCategories.length === 0) {
        items.push({
          id: cluster.id,
          type: "cluster",
          label: cluster.label,
          description: cluster.description,
          imageUrl: cluster.image_url,
          avg: getClusterAvg(cluster.id),
          user: userClusterAllocations[cluster.id] || 0,
        });
      } else {
        cluster.subCategories.forEach((sub) => {
          items.push({
            id: `${cluster.id}-${sub.id}`,
            type: "sub",
            label: sub.label,
            parentLabel: cluster.label,
            description: sub.description,
            imageUrl: cluster.image_url,
            avg: getSubAvg(sub.id),
            user: userSubAllocations[sub.id] || 0,
          });
        });
      }
    });

    return items;
  }, [clusters, getClusterAvg, getSubAvg, userClusterAllocations, userSubAllocations]);
  const sortedGranularItems = useMemo(() => {
    return [...granularItems].sort((a, b) => {
      const aValue = sortBy === "user" ? a.user : a.avg;
      const bValue = sortBy === "user" ? b.user : b.avg;
      if (bValue !== aValue) return bValue - aValue;
      return a.label.localeCompare(b.label);
    });
  }, [granularItems, sortBy]);
  const clusterScatterPoints = useMemo<ConstantSumScatterPoint[]>(
    () =>
      clusters.map((cluster) => {
        const averageValue = clusterAverageMap.get(cluster.id) ?? 0;
        const userValue = userClusterAllocations[cluster.id] || 0;
        return {
          optionId: cluster.id,
          label: cluster.label,
          imageUrl: cluster.image_url,
          description: cluster.description,
          averageValue,
          userValue,
          xValue: averageValue,
          yValue: userValue,
          isHighlighted: userValue > 0 || hasSubmitted,
        };
      }),
    [clusterAverageMap, clusters, hasSubmitted, userClusterAllocations]
  );
  const subScatterPoints = useMemo<ConstantSumScatterPoint[]>(() => {
    return clusters.flatMap((cluster) =>
      cluster.subCategories.map((sub) => {
        const averageValue = subAverageMap.get(sub.id) ?? 0;
        const userValue = userSubAllocations[sub.id] || 0;
        return {
          optionId: `${cluster.id}-${sub.id}`,
          label: sub.label,
          parentLabel: cluster.label,
          imageUrl: cluster.image_url,
          description: sub.description,
          averageValue,
          userValue,
          xValue: averageValue,
          yValue: userValue,
          isHighlighted: userValue > 0 || hasSubmitted,
        };
      })
    );
  }, [clusters, hasSubmitted, subAverageMap, userSubAllocations]);
  const activeScatterPoints = useMemo<ConstantSumScatterPoint[]>(() => {
    if (scatterDisplayMode === "clusters_only") return clusterScatterPoints;
    const clustersWithSubs = new Set(clusters.filter((cluster) => cluster.subCategories.length > 0).map((cluster) => cluster.id));
    const clustersWithoutSubs = clusterScatterPoints.filter((point) => !clustersWithSubs.has(point.optionId ?? ""));
    return [...subScatterPoints, ...clustersWithoutSubs];
  }, [clusterScatterPoints, clusters, scatterDisplayMode, subScatterPoints]);
  const scatterMaxValue = useMemo(() => {
    const values = activeScatterPoints.flatMap((point) => [point.xValue, point.yValue]);
    const maxValue = Math.max(...values, 0);
    const padded = Math.max(10, maxValue);
    return padded;
  }, [activeScatterPoints]);

  if (loading)
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size={120} />
      </div>
    );

  if (totalResponses === 0) {
    // Check if we have responses but missing allocations (data integrity issue)
    if (actualResponseCount > 0) {
      return (
        <div className="text-center py-12">
          <p className="text-lg font-medium text-destructive">Data Error: Allocations not found</p>
          <p className="text-sm text-muted-foreground mt-2">
            {actualResponseCount} response(s) recorded but allocation data is missing.
            This may have been caused by poll configuration changes.
          </p>
        </div>
      );
    }
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium">No responses yet</p>
        <p className="text-sm text-muted-foreground mt-2">Be the first to share your perspective!</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Card className="p-4 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-1">Results</h3>
          <p className="text-sm text-muted-foreground">
            Based on {totalResponses} response{totalResponses !== 1 ? "s" : ""}
          </p>
        </div>

        {resolvedViewMode === "scatter_chart" ? (
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-1">
                <h4 className="text-base font-semibold">Scatter view</h4>
                <p className="text-sm text-muted-foreground">Compare your allocation against the community average.</p>
              </div>
              {hasAnySubcategories && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Show</span>
                  <Select
                    value={scatterDisplayMode}
                    onValueChange={(value) => setScatterDisplayMode(value as ScatterDisplayMode)}
                  >
                    <SelectTrigger className="h-9 w-[280px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="subcategories_plus_clusters">
                        <div>
                          <p className="font-medium leading-none">Sub-categories + clusters without sub-categories</p>
                          <p className="text-xs text-muted-foreground">Plot every sub-category and any clusters that have no sub-categories.</p>
                        </div>
                      </SelectItem>
                      <SelectItem value="clusters_only">
                        <div>
                          <p className="font-medium leading-none">Clusters only</p>
                          <p className="text-xs text-muted-foreground">View the allocations at the cluster level.</p>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <ConstantSumScatterPlot
              data={activeScatterPoints}
              xLabel="Average allocation"
              yLabel="Your allocation"
              maxValue={scatterMaxValue}
            />

            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                Points on the diagonal show where your allocation matches the crowd average. Items in the upper left
                are where you allocate more than average, while those in the lower right are below the community
                baseline.
              </p>
              {!hasSubmitted && (
                <p>
                  Submit your allocation to plot your own responses on the chart. Until then, the vertical axis reflects
                  your current selections.
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <h4 className="text-base font-semibold">Allocation summary</h4>
                  <p className="text-sm text-muted-foreground">Compare your allocation against the community average.</p>
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                  {hasAnySubcategories && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">Show</span>
                      <Select
                        value={allocationSummaryDisplayMode}
                        onValueChange={(value) =>
                          setAllocationSummaryDisplayMode(value as AllocationSummaryDisplayMode)
                        }
                      >
                        <SelectTrigger className="h-9 w-[240px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="combined">
                            <div>
                              <p className="font-medium leading-none">Combined</p>
                              <p className="text-xs text-muted-foreground">Show clusters with their sub-categories.</p>
                            </div>
                          </SelectItem>
                          <SelectItem value="clusters_only">
                            <div>
                              <p className="font-medium leading-none">Clusters only</p>
                              <p className="text-xs text-muted-foreground">Focus on the highest-level allocations.</p>
                            </div>
                          </SelectItem>
                          <SelectItem value="most_granular">
                            <div>
                              <p className="font-medium leading-none">Most granular</p>
                              <p className="text-xs text-muted-foreground">
                                Show every sub-category and clusters without sub-categories.
                              </p>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(allocationSummaryDisplayMode === "clusters_only" ||
                    allocationSummaryDisplayMode === "most_granular") && (
                    <div className="flex items-center gap-2 text-sm">
                      <label htmlFor="allocation-sort" className="text-muted-foreground whitespace-nowrap">
                        Sort by
                      </label>
                      <select
                        id="allocation-sort"
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value as "average" | "user")}
                        className="h-9 rounded-md border bg-background px-3 text-sm"
                      >
                        <option value="average">Response average</option>
                        <option value="user">Your allocation</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
              {allocationSummaryDisplayMode === "most_granular" ? (
                <div className="space-y-3">
                  {sortedGranularItems.map((item) => {
                    const barValue = Math.min(item.user, 100);
                    const avgValue = Math.min(item.avg, 100);

                    return (
                      <div key={item.id} className="space-y-3 border border-border rounded-lg p-4 bg-card/50">
                        <div className="grid grid-cols-[2.75rem_1fr] gap-3">
                          <div className="pt-0.5">
                            {item.imageUrl && (
                              <img
                                src={item.imageUrl}
                                alt={item.label}
                                className="w-10 h-10 object-cover rounded-md"
                              />
                            )}
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <span className="font-medium text-base">{item.label}</span>
                                  {item.description && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-xs text-sm">{item.description}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                                {item.parentLabel && (
                                  <p className="text-xs text-muted-foreground">Cluster: {item.parentLabel}</p>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground whitespace-nowrap">
                                Avg: {item.avg.toFixed(1)} | You: {item.user}
                              </div>
                            </div>

                            <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full">
                              <div
                                className="absolute top-0 left-0 h-full bg-blue-500/80"
                                style={{ width: `${barValue}%` }}
                              />
                              {item.avg > 0 && (
                                <div
                                  className="absolute -top-1 -bottom-1 w-[2px] z-10"
                                  style={{
                                    left: `calc(${avgValue}% - 1px)`,
                                    backgroundColor: pollColorPalette.averageIndicator,
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-5">
                  {(allocationSummaryDisplayMode === "clusters_only" ? sortedClusters : clusters).map((cluster) => {
                    const clusterAvg = getClusterAvg(cluster.id);
                    const clusterUser = userClusterAllocations[cluster.id] || 0;
                    const subTrackWidth = Math.min(100, Math.max(clusterUser, clusterAvg, 10));
                    const showSubCategories = allocationSummaryDisplayMode === "combined";

                    return (
                      <div key={cluster.id} className="space-y-3 border border-border rounded-lg p-4 bg-card/50">
                        <div className="grid grid-cols-[2.75rem_1fr] gap-3">
                          <div className="pt-0.5">
                            {cluster.image_url && (
                              <img
                                src={cluster.image_url}
                                alt={cluster.label}
                                className="w-10 h-10 object-cover rounded-md"
                              />
                            )}
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1">
                                <span className="font-medium text-base">{cluster.label}</span>
                                {cluster.description && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs text-sm">{cluster.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground whitespace-nowrap">
                                Avg: {clusterAvg.toFixed(1)} | You: {clusterUser}
                              </div>
                            </div>

                            <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full">
                              <div
                                className="absolute top-0 left-0 h-full bg-blue-500/80"
                                style={{ width: `${Math.min(clusterUser, 100)}%` }}
                              />
                              {clusterAvg > 0 && (
                                <div
                                  className="absolute -top-1 -bottom-1 w-[2px] z-10"
                                  style={{
                                    left: `calc(${Math.min(clusterAvg, 100)}% - 1px)`,
                                    backgroundColor: pollColorPalette.averageIndicator,
                                  }}
                                />
                              )}
                            </div>

                            {showSubCategories && (
                              <div className="space-y-2">
                                {cluster.subCategories.map((sub) => {
                                  const subUser = userSubAllocations[sub.id] || 0;
                                  const subAvg = getSubAvg(sub.id);
                                  const spanDenominator = Math.max(clusterAvg, clusterUser, 1);
                                  const baseWidth = subTrackWidth;
                                  const userWidth = (subUser / spanDenominator) * baseWidth;
                                  const avgPosition = (subAvg / spanDenominator) * baseWidth;

                                  return (
                                    <div key={sub.id} className="space-y-1">
                                      <div className="flex items-center justify-between text-sm">
                                        <div className="flex items-center gap-1">
                                          <span>{sub.label}</span>
                                          {sub.description && (
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <Info className="w-3 h-3 text-muted-foreground cursor-pointer" />
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p className="max-w-xs text-sm">{sub.description}</p>
                                              </TooltipContent>
                                            </Tooltip>
                                          )}
                                        </div>
                                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                                          Avg: {subAvg.toFixed(1)} | You: {subUser}
                                        </div>
                                      </div>

                                      <div className="relative h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                                        <div
                                          className="absolute inset-0 bg-muted/60"
                                          style={{ width: `${baseWidth}%` }}
                                        />
                                        <div
                                          className="absolute top-0 left-0 h-full bg-blue-500"
                                          style={{ width: `${Math.min(userWidth, 100)}%` }}
                                        />
                                        {subAvg > 0 && (
                                          <div
                                            className="absolute -top-1 -bottom-1 w-[2px] z-10"
                                            style={{
                                              left: `calc(${Math.min(avgPosition, 100)}% - 1px)`,
                                              backgroundColor: pollColorPalette.averageIndicator,
                                            }}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 pt-3 border-t border-border text-xs text-muted-foreground sm:flex-row sm:items-center sm:gap-6">
              <div className="flex items-center gap-1">
                <div className="w-4 h-2 bg-blue-500 rounded-sm"></div>
                <span>Your allocation</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-10 h-3 rounded-full bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 bg-blue-500/40 rounded-full" />
                  <div
                    className="absolute -top-1 -bottom-1 w-[2px]"
                    style={{ left: "calc(60% - 1px)", backgroundColor: pollColorPalette.averageIndicator }}
                  />
                </div>
                <span>Global average</span>
              </div>
            </div>
          </>
        )}
      </Card>
    </TooltipProvider>
  );
};
